import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { useMediaQuery } from "@/utils/hooks";
import {
  forwardRef,
  useContext,
  ComponentPropsWithoutRef,
  ElementRef,
  PropsWithChildren,
} from "react";
import {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogPortal,
  DialogOverlay,
} from "@/kit/ui/dialog";
import {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerHeader,
  DrawerFooter,
  DrawerPortal,
  DrawerOverlay,
} from "@/kit/ui/drawer";
import { ResponsiveModalContext } from "@/kit/helpers/responsive-modal";
import { Primitive } from "@radix-ui/react-primitive";

export interface ResponsiveModalProps extends PropsWithChildren {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ResponsiveModal = ({
  children,
  open,
  onOpenChange,
}: ResponsiveModalProps) => {
  const isDialog = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const Comp = isDialog ? Dialog : Drawer;
  return (
    <ResponsiveModalContext.Provider
      value={{ isDialog, setOpen: onOpenChange }}
    >
      <Comp open={open} onOpenChange={onOpenChange}>
        {children}
      </Comp>
    </ResponsiveModalContext.Provider>
  );
};

export const ResponsiveModalPortal = ({ children }: PropsWithChildren) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogPortal : DrawerPortal;
  return <Comp>{children}</Comp>;
};

export const ResponsiveModalTrigger = forwardRef<
  ElementRef<typeof Primitive.button>,
  ComponentPropsWithoutRef<typeof Primitive.button>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogTrigger : DrawerTrigger;
  return <Comp {...props} ref={ref} />;
});

export const ResponsiveModalOverlay = forwardRef<
  ElementRef<typeof DialogOverlay>,
  ComponentPropsWithoutRef<typeof DialogOverlay>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogOverlay : DrawerOverlay;
  return <Comp {...props} ref={ref} />;
});

export const ResponsiveModalClose = forwardRef<
  ElementRef<typeof DialogClose>,
  ComponentPropsWithoutRef<typeof DialogClose>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogClose : DrawerClose;
  return <Comp {...props} ref={ref} />;
});

export const ResponsiveModalCloseDialog = forwardRef<
  ElementRef<typeof DialogClose>,
  ComponentPropsWithoutRef<typeof DialogClose>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  if (!isDialog) return null;
  return <DialogClose {...props} ref={ref} />;
});

export const ResponsiveModalCloseDrawer = forwardRef<
  ElementRef<typeof DrawerClose>,
  ComponentPropsWithoutRef<typeof DrawerClose>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  if (isDialog) return null;
  return <DrawerClose {...props} ref={ref} />;
});

export const ResponsiveModalContent = forwardRef<
  ElementRef<typeof DialogContent>,
  Omit<ComponentPropsWithoutRef<typeof DialogContent>, "onAnimationEnd">
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogContent : DrawerContent;
  return <Comp {...props} ref={ref} />;
});

export const ResponsiveModalDescription = forwardRef<
  ElementRef<typeof DialogDescription>,
  ComponentPropsWithoutRef<typeof DialogDescription>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogDescription : DrawerDescription;
  return <Comp {...props} ref={ref} />;
});

export const ResponsiveModalTitle = forwardRef<
  ElementRef<typeof DialogTitle>,
  ComponentPropsWithoutRef<typeof DialogTitle>
>((props, ref) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogTitle : DrawerTitle;
  return <Comp {...props} ref={ref} />;
});

export const ResponsiveModalHeader = (
  props: ComponentPropsWithoutRef<typeof DialogHeader>,
) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogHeader : DrawerHeader;
  return <Comp {...props} />;
};

export const ResponsiveModalFooter = (
  props: ComponentPropsWithoutRef<typeof DialogFooter>,
) => {
  const { isDialog } = useContext(ResponsiveModalContext);
  const Comp = isDialog ? DialogFooter : DrawerFooter;
  return <Comp {...props} />;
};
